import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal, Input, Textarea, Button, Alert } from '@components/common'
import { PermissionEditor } from '@components/features/permissions'
import { roleService } from '@services/roleService'
import toast from 'react-hot-toast'
import './CompanyRoles.scss'

/**
 * Role Form Validation Schema
 */
const roleSchema = z.object({
	name: z.string().min(3, 'Name must be at least 3 characters').max(100, 'Name too long'),
	slug: z
		.string()
		.min(3, 'Slug must be at least 3 characters')
		.max(100, 'Slug too long')
		.regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
	description: z.string().min(10, 'Description must be at least 10 characters').optional().or(z.literal('')),
})

/**
 * CompanyRoleForm Component
 *
 * Modal form for creating and editing custom roles
 * System roles cannot be edited - this form is only for custom roles
 *
 * @param {Object} props
 * @param {string} props.mode - Form mode ('create' or 'edit')
 * @param {number} props.companyId - Company ID for the role
 * @param {Object} props.roleData - Role data for editing (optional)
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSuccess - Success callback
 */
/**
 * Check if a role is a system default role
 * Handles string "1"/"0", number 1/0, and boolean true/false
 */
const checkIsSystemRole = (role) => {
	const value = role?.is_system_default
	return value === true || value === 1 || value === '1'
}

const CompanyRoleForm = ({ mode = 'create', companyId, roleData, onClose, onSuccess }) => {
	const isEditMode = mode === 'edit'
	const [loading, setLoading] = useState(false)
	const [activeTab, setActiveTab] = useState('details')

	// Check if this is a system role (should not be editable)
	const isSystemRole = checkIsSystemRole(roleData)

	// Permissions state (separate from react-hook-form due to complex structure)
	const [permissions, setPermissions] = useState({})

	// React Hook Form setup
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
	} = useForm({
		resolver: zodResolver(roleSchema),
		defaultValues: {
			name: '',
			slug: '',
			description: '',
		},
	})

	// Populate form in edit mode
	useEffect(() => {
		if (isEditMode && roleData) {
			setValue('name', roleData.name || '')
			setValue('slug', roleData.slug || '')
			setValue('description', roleData.description || '')
			// Parse permissions - handle both string and object
			const rolePermissions =
				typeof roleData.permissions === 'string'
					? JSON.parse(roleData.permissions || '{}')
					: roleData.permissions || {}
			setPermissions(rolePermissions)
		}
	}, [isEditMode, roleData, setValue])

	/**
	 * Handle form submission
	 */
	const onSubmit = async (data) => {
		if (isSystemRole) {
			toast.error('System roles cannot be modified')
			return
		}

		setLoading(true)
		try {
			if (isEditMode) {
				// Don't send slug when updating (it's immutable)
				const { slug, ...updateData } = data
				await roleService.update(roleData.id, {
					...updateData,
					permissions: permissions,
				})
				toast.success('Role updated successfully!')
			} else {
				// Add company_id and permissions for new custom roles
				const createData = {
					...data,
					company_id: companyId,
					permissions: permissions,
				}
				await roleService.create(createData)
				toast.success('Role created successfully!')
			}

			reset()
			setPermissions({})
			onSuccess?.()
		} catch (error) {
			console.error('Form submission error:', error)
			const errorMessage = error.response?.data?.message || error.message || 'Failed to save role'
			toast.error(errorMessage)
		} finally {
			setLoading(false)
		}
	}

	/**
	 * Generate slug from name
	 */
	const handleNameChange = (e) => {
		const name = e.target.value
		if (!isEditMode) {
			const slug = name
				.toLowerCase()
				.replace(/[^a-z0-9\s-]/g, '')
				.replace(/\s+/g, '-')
				.replace(/-+/g, '-')
			setValue('slug', slug)
		}
	}

	// Calculate permission count
	const permissionCount = Object.values(permissions).reduce(
		(sum, actions) => sum + (Array.isArray(actions) ? actions.length : 0),
		0
	)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			size="large"
			title={isEditMode ? 'Edit Custom Role' : 'Create Custom Role'}
			closeOnBackdrop={false}
			closeOnEscape={false}
			footer={
				<>
					<Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
						Cancel
					</Button>
					<div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
						<Button
							type="submit"
							variant="primary"
							loading={loading}
							disabled={isSystemRole}
							onClick={handleSubmit(onSubmit)}
						>
							{isEditMode ? 'Update Role' : 'Create Role'}
						</Button>
					</div>
				</>
			}
		>
			<div className="role-form">
				{/* System Role Warning */}
				{isSystemRole && (
					<Alert
						variant="warning"
						className="role-form__warning"
						message="System roles cannot be edited. Clone this role to create a customizable version."
						dismissible={false}
					/>
				)}

				{/* Tabs */}
				<div className="role-form__tabs">
					<button
						type="button"
						className={`role-form__tab ${activeTab === 'details' ? 'role-form__tab--active' : ''}`}
						onClick={() => setActiveTab('details')}
					>
						Role Details
					</button>
					<button
						type="button"
						className={`role-form__tab ${activeTab === 'permissions' ? 'role-form__tab--active' : ''}`}
						onClick={() => setActiveTab('permissions')}
					>
						Permissions
						{permissionCount > 0 && <span className="role-form__tab-badge">{permissionCount}</span>}
					</button>
				</div>

				{/* Details Tab */}
				{activeTab === 'details' && (
					<form onSubmit={handleSubmit(onSubmit)} className="role-form__fields">
						{/* Role Name */}
						<Input
							label="Role Name"
							placeholder="e.g., Quality Assurance Lead"
							{...register('name')}
							onChange={(e) => {
								register('name').onChange(e)
								handleNameChange(e)
							}}
							error={errors.name?.message}
							required
							disabled={isSystemRole || loading}
						/>

						{/* Slug */}
						<Input
							label="Slug"
							placeholder="e.g., quality-assurance-lead"
							{...register('slug')}
							error={errors.slug?.message}
							required
							disabled={isEditMode || isSystemRole || loading}
							helperText={isEditMode ? 'Slug cannot be changed after creation' : 'Auto-generated from name'}
						/>

						{/* Description */}
						<Textarea
							label="Description"
							placeholder="Describe the role and its responsibilities..."
							rows={4}
							error={errors.description?.message}
							disabled={isSystemRole || loading}
							{...register('description')}
						/>
					</form>
				)}

				{/* Permissions Tab */}
				{activeTab === 'permissions' && (
					<div className="role-form__permissions">
						<PermissionEditor
							value={permissions}
							onChange={setPermissions}
							disabled={isSystemRole || loading}
							showSearch={true}
							expandedByDefault={false}
						/>
					</div>
				)}
			</div>
		</Modal>
	)
}

CompanyRoleForm.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	companyId: PropTypes.number.isRequired,
	roleData: PropTypes.shape({
		id: PropTypes.number,
		name: PropTypes.string,
		slug: PropTypes.string,
		description: PropTypes.string,
		is_system_default: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
	}),
	onClose: PropTypes.func.isRequired,
	onSuccess: PropTypes.func,
}

export default CompanyRoleForm
